import ODataUtil from "./ODataUtil";
import { deleteItem, patchItem, postItem, ApiRequest } from './ApiUtils';
import WorkforceLabor from 'classes/WorkforceLabor';
import buildQuery from 'odata-query';

const workforceLaborEndpoint = "/WorkforceLabor";

/**
 * Convert workforce labor data to table schema, flattening nested fields
 */
function convertWorkforceLaborToTableSchema({ "value": items, "@odata.count": count }) {
  const flattenedItems = items.map(item => {
    const flattened = { ...item };

    // Flatten Inspection Number and Inspector name from nested expand
    if (item.Ihwi && item.Ihwi.Inspection) {
      flattened.inspectionNumber = item.Ihwi.Inspection.InspectionNumber;
      if (item.Ihwi.Inspection.Rassignment && item.Ihwi.Inspection.Rassignment.SystemUser) {
        flattened.inspectorName = item.Ihwi.Inspection.Rassignment.SystemUser.Name;
      }
    }

    // Flatten Contractor Name from nested expand
    if (item.ContractorService && item.ContractorService.ContractorProject &&
        item.ContractorService.ContractorProject.ContractorNavigation) {
      flattened.contractorName = item.ContractorService.ContractorProject.ContractorNavigation.Description;
    }

    // Count Personnel (Laborer records)
    flattened.personnelCount = item.Laborer ? item.Laborer.length : 0;

    return flattened;
  });

  const { data } = WorkforceLabor.convertToTableRows({ "value": flattenedItems, "@odata.count": count });
  return { data, count };
}

/**
 * Get workforce labor with OData query support
 */
async function getWorkforceLabor(page, per_page, orderByInfo, orderByDirection, search, filters) {
  try {
    const { skip, count, top, expand, orderBy, mappedFilter } = ODataUtil.UtilityBuilderNestedv3(
      per_page, page, WorkforceLabor.expand, orderByInfo, orderByDirection, filters
    );
    const filter = {};
    const isDefaults = filters.find(r => r.defaults);

    if (isDefaults) {
      const defaults = isDefaults.defaults;
      if(defaults.projectId) filter["ihwi/workItem/projectId"] = { eq: { type: 'guid', value: defaults.projectId }};
      if(defaults.inspectionNumber) filter["ihwi/inspection/inspectionNumber"] = defaults.inspectionNumber;
    }

    const allFilters = mappedFilter.reduce((a, b) => ({...a, ...b}), filter);
    const params = buildQuery({ count, top, skip, orderBy, expand, filter: allFilters });

    const data = await ApiRequest(workforceLaborEndpoint, params);
    return convertWorkforceLaborToTableSchema(data);
  } catch (error) {
    console.error('Error fetching workforce labor:', error);
    throw error;
  }
}

/**
 * Add a new workforce labor
 */
async function addWorkforceLabor(data) {
  try {
    const resourcePath = `${workforceLaborEndpoint}/Add`;
    const result = await postItem(resourcePath, '', data);
    return result;
  } catch (error) {
    console.error('Error adding workforce labor:', error);
    throw error;
  }
}

/**
 * Update an existing workforce labor
 */
async function updateWorkforceLabor(workforceLaborId, data) {
  try {
    const resourcePath = `${workforceLaborEndpoint}/Update`;
    const params = `?workforceLaborId=${workforceLaborId}`;
    const result = await patchItem(resourcePath, params, data);
    return result;
  } catch (error) {
    console.error('Error updating workforce labor:', error);
    throw error;
  }
}

/**
 * Delete a workforce labor
 */
async function deleteWorkforceLabor(workforceLaborId) {
  try {
    const resourcePath = `${workforceLaborEndpoint}/Delete`;
    const params = `?workforceLaborId=${workforceLaborId}`;
    const result = await deleteItem(resourcePath, params);
    return result;
  } catch (error) {
    console.error('Error deleting workforce labor:', error);
    throw error;
  }
}

/**
 * Get a specific workforce labor by ID
 */
async function getWorkforceLaborById(workforceLaborId) {
  try {
    const expand = WorkforceLabor.expand;
    const { expand: expandQuery } = ODataUtil.UtilityBuilderNestedv3(
      1,
      0,
      expand,
      null,
      null,
      []
    );

    const params = buildQuery({
      expand: expandQuery,
      filter: { WorkforceLaborId: { eq: { type: 'guid', value: workforceLaborId } } }
    });

    const response = await fetch(`${workforceLaborEndpoint}${params}`);
    const result = await response.json();

    if (result.value && result.value.length > 0) {
      return new WorkforceLabor(result.value[0]);
    }

    return null;
  } catch (error) {
    console.error('Error fetching workforce labor by ID:', error);
    throw error;
  }
}

export {
  getWorkforceLabor,
  addWorkforceLabor,
  updateWorkforceLabor,
  deleteWorkforceLabor,
  getWorkforceLaborById
};
